import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { clientsService } from '../services/api';
import { RateioDoGrupo, normalizarRateio } from '../utils/rateioDoGrupo';
import { ASSINATURA_GRAVADA } from '../utils/avisoDeGravacao';

/**
 * UMA LEITURA DE RATEIO POR GRUPO.
 *
 * Até 04/10/2026, cinco lugares chamavam `getRateio` cada um por si: a ficha
 * (`rateioDaFicha`), a edição (`rateioDaAssinatura`), `useGruposEconomicos`, o
 * `PainelDoRateio` e o `PainelQuemUsaQuemPaga`. Na tela de Grupos Econômicos, o
 * mesmo grupo era lido duas vezes; numa ficha de grupo, até três.
 *
 * Aqui há um cache por (empresa, competência) com a PROMESSA, não só o resultado:
 * chamadas simultâneas compartilham a mesma requisição. E como a API resolve a
 * matriz a partir de qualquer membro e devolve o grupo inteiro, a primeira
 * resposta já preenche o cache de TODAS as empresas do grupo.
 *
 * Validade curta (60s): o rateio muda raramente, mas uma tela aberta por muito
 * tempo não deve mostrar versão velha para sempre. `esquecerRateio()` limpa tudo
 * E avisa quem está na tela: `useRateio` e `useGruposEconomicos` leem de novo sem
 * recarregar a página. Chamam: gravação de rateio, competência passada e qualquer
 * escrita em /contracts (evento ASSINATURA_GRAVADA, do interceptor do axios).
 */
const VALIDADE_MS = 60_000;

interface Entrada {
  promessa: Promise<RateioDoGrupo>;
  em: number;
}

const cache = new Map<string, Entrada>();
const chave = (clientId: number, competencia?: string | null) => `${clientId}|${competencia || ''}`;

/** Ids de todas as empresas que a resposta cobre (matriz e linhas do rateio). */
function empresasDaResposta(r: RateioDoGrupo): number[] {
  const ids = new Set<number>([r.matrizId]);
  if (r.temRateio !== false) for (const l of r.linhas) ids.add(l.clientId);
  return Array.from(ids);
}

export function lerRateio(clientId: number, competencia?: string | null): Promise<RateioDoGrupo> {
  const k = chave(clientId, competencia);
  const agora = Date.now();
  const atual = cache.get(k);
  if (atual && agora - atual.em < VALIDADE_MS) return atual.promessa;

  const promessa = clientsService.getRateio(clientId, competencia || undefined).then((bruto: any) => {
    const r = normalizarRateio(bruto);
    // A mesma resposta vale para todas as empresas do grupo.
    for (const id of empresasDaResposta(r)) {
      if (id !== clientId) cache.set(chave(id, competencia), { promessa: Promise.resolve(r), em: Date.now() });
    }
    return r;
  });
  // Falha não fica no cache: a próxima chamada tenta de novo.
  promessa.catch(() => { if (cache.get(k)?.promessa === promessa) cache.delete(k); });
  cache.set(k, { promessa, em: agora });
  return promessa;
}

let versao = 0;
const ouvintes = new Set<() => void>();

/** Limpa o cache inteiro e avisa quem está mostrando rateio, que lê de novo. */
export function esquecerRateio(): void {
  cache.clear();
  versao += 1;
  ouvintes.forEach((f) => f());
}

if (typeof window !== 'undefined') {
  window.addEventListener(ASSINATURA_GRAVADA, () => esquecerRateio());
}

const inscrever = (f: () => void) => { ouvintes.add(f); return () => { ouvintes.delete(f); }; };

/** Muda toda vez que o rateio é esquecido: dependa dela para ler de novo. */
export function useVersaoDoRateio(): number {
  return useSyncExternalStore(inscrever, () => versao, () => versao);
}

/**
 * O rateio do grupo de uma empresa. `clientId` vazio = nada a ler.
 * `erro` vem preenchido quando a leitura falha; quem não precisa mostrar o erro
 * trata como "sem rateio", como antes.
 */
export function useRateio(clientId: number | null | undefined, competencia?: string | null) {
  const [rateio, setRateio] = useState<RateioDoGrupo | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [carregando, setCarregando] = useState(false);
  const versaoAtual = useVersaoDoRateio();
  // Releitura pela MESMA empresa (assinatura gravada) mantém o rateio na tela até
  // a resposta nova chegar; troca de empresa ou competência limpa na hora.
  const ultimaChave = useRef<string | null>(null);

  useEffect(() => {
    const k = chave(clientId || 0, competencia);
    if (ultimaChave.current !== k) {
      ultimaChave.current = k;
      setRateio(null);
      setErro(null);
    }
    if (!clientId) { setCarregando(false); return; }
    let vivo = true;
    setCarregando(true);
    lerRateio(clientId, competencia)
      .then((r) => { if (vivo) setRateio(r); })
      .catch((e: any) => { if (vivo) setErro(e?.response?.data?.message || e?.message || 'Não foi possível ler o rateio.'); })
      .finally(() => { if (vivo) setCarregando(false); });
    return () => { vivo = false; };
  }, [clientId, competencia, versaoAtual]);

  return { rateio, erro, carregando };
}
